
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { FileText, MessageCircle, Video, Download, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Post {
  id: string;
  content: string;
  image: string;
  isVideo?: boolean;
  timestamp: string;
  likes: number;
  comments: number;
  whatsHappening?: string;
  challenges?: string;
}

interface ArchiveCropDialogProps {
  isOpen: boolean;
  onClose: () => void;
  cropName: string;
  cropId: string;
  relatedPosts: Post[];
  onConfirmRemove: () => void;
}

export const ArchiveCropDialog = ({ 
  isOpen, 
  onClose, 
  cropName, 
  cropId, 
  relatedPosts, 
  onConfirmRemove 
}: ArchiveCropDialogProps) => {
  const [selectedPosts, setSelectedPosts] = useState<string[]>([]);
  const [isArchiving, setIsArchiving] = useState(false);
  const { toast } = useToast();

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPosts(relatedPosts.map(post => post.id));
    } else {
      setSelectedPosts([]);
    }
  };

  const handlePostSelect = (postId: string, checked: boolean) => {
    if (checked) {
      setSelectedPosts(prev => [...prev, postId]);
    } else {
      setSelectedPosts(prev => prev.filter(id => id !== postId));
    }
  };

  const handleArchiveAndRemove = async () => {
    setIsArchiving(true);
    
    try {
      if (selectedPosts.length > 0) {
        // Create archive data
        const archiveData = {
          cropName,
          cropId,
          archivedAt: new Date().toISOString(),
          posts: relatedPosts.filter(post => selectedPosts.includes(post.id))
        };

        // Download archive as JSON file
        const blob = new Blob([JSON.stringify(archiveData, null, 2)], { 
          type: 'application/json' 
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${cropName.toLowerCase().replace(/\s+/g, '-')}-archive-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
          title: "Archive Created",
          description: `${selectedPosts.length} posts archived for ${cropName}`,
        });
      }

      // Proceed with crop removal
      onConfirmRemove();
      onClose();
    } catch (error) {
      console.error("Error archiving posts:", error);
      toast({
        title: "Archive Error",
        description: "Failed to create archive. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsArchiving(false);
    }
  };

  const handleRemoveWithoutArchive = () => {
    onConfirmRemove();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-600" />
            Remove {cropName} from Crop Plan
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> You're about to remove "{cropName}" from your crop plan. 
              You have {relatedPosts.length} related post{relatedPosts.length !== 1 ? 's' : ''} that will remain in your feed.
            </p>
          </div>

          {relatedPosts.length > 0 && (
            <>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Archive Related Content</h4>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="select-all"
                      checked={selectedPosts.length === relatedPosts.length}
                      onCheckedChange={handleSelectAll}
                    />
                    <label htmlFor="select-all" className="text-sm text-gray-600">
                      Select All
                    </label>
                  </div>
                </div>

                <ScrollArea className="h-64 border rounded-lg p-3">
                  <div className="space-y-3">
                    {relatedPosts.map((post) => (
                      <div key={post.id} className="flex items-start space-x-3 p-3 border rounded-lg bg-gray-50">
                        <Checkbox
                          id={`post-${post.id}`}
                          checked={selectedPosts.includes(post.id)}
                          onCheckedChange={(checked) => handlePostSelect(post.id, !!checked)}
                        />
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {post.isVideo ? (
                              <Video className="h-4 w-4 text-blue-600" />
                            ) : (
                              <FileText className="h-4 w-4 text-gray-600" />
                            )}
                            <span className="text-xs text-gray-500">{post.timestamp}</span>
                          </div>
                          
                          <p className="text-sm text-gray-800 line-clamp-2 mb-2">
                            {post.content}
                          </p>
                          
                          {post.whatsHappening && (
                            <div className="text-xs text-blue-700 bg-blue-50 p-1 rounded mb-1">
                              <strong>What's happening:</strong> {post.whatsHappening}
                            </div>
                          )}
                          
                          {post.challenges && (
                            <div className="text-xs text-yellow-700 bg-yellow-50 p-1 rounded mb-1">
                              <strong>Challenges:</strong> {post.challenges}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>❤️ {post.likes}</span>
                            <span>💬 {post.comments}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                {selectedPosts.length > 0 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-green-800">
                      <Download className="h-4 w-4" />
                      {selectedPosts.length} post{selectedPosts.length !== 1 ? 's' : ''} will be archived as a JSON file
                    </div>
                  </div>
                )}
              </div>

              <Separator />
            </>
          )}

          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            
            {relatedPosts.length > 0 && (
              <Button
                onClick={handleArchiveAndRemove}
                disabled={isArchiving}
                className="bg-green-600 hover:bg-green-700"
              >
                {isArchiving ? "Archiving..." : 
                 selectedPosts.length > 0 ? `Archive ${selectedPosts.length} & Remove` : "Skip Archive & Remove"}
              </Button>
            )}
            
            <Button
              variant="destructive"
              onClick={handleRemoveWithoutArchive}
              disabled={isArchiving}
            >
              Remove Without Archive
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
